/**
 * Convert a single Tripetto condition to a JavaScript expression
 * Routes to specialized converters based on condition type
 */

import { Observable } from 'rxjs';
import { NodeToFieldMap } from './node-field-map';
import { OptionValueProp } from '@nx-ahs/formly/shared';
import { convertTextCondition, TextConditionProps } from './block-condition-converters/text-condition-converter';
import { convertNumberCondition, NumberConditionProps } from './block-condition-converters/number-condition-converter';
import { convertDateCondition, DateConditionProps } from './block-condition-converters/date-condition-converter';
import {
	convertSelectionCondition,
	SelectionConditionProps
} from './block-condition-converters/selection-condition-converter';
import { convertSimpleCondition, SimpleConditionProps } from './block-condition-converters/simple-condition-converter';
import { convertChipCondition, ChipConditionProps } from './block-condition-converters/chip-condition-converter';
import { convertArrayCondition, ArrayConditionProps } from './block-condition-converters/array-condition-converter';
import { convertEmailCondition, EmailConditionProps } from './block-condition-converters/email-condition-converter';
import {
	convertEqualsCondition,
	convertNotEqualsCondition,
	convertContainsCondition,
	convertNotContainsCondition
} from './legacy-condition-helpers';

export interface TripettoCondition {
	block?: {
		type?: string;
		node?: string;
		slot?: string;
		match?: any;
		value?: any;
		mode?: string;
		button?: {
			id: string;
			name: string;
			value?: string;
		};
		checkbox?: {
			id: string;
			name: string;
			value?: string;
		};
		checked?: boolean;
		[key: string]: any;
	};
	[key: string]: any;
}

/**
 * Helper function to find an option value from Formly field options
 * Only works with static arrays, not Observables
 */
export function findOptionValue(options: any[] | Observable<any[]> | undefined, searchValue: any): any | undefined {
	// Only process if options is a static array (not Observable)
	if (!options || options instanceof Observable) {
		return undefined;
	}

	// Type guard: ensure it's an array
	if (!Array.isArray(options)) {
		return undefined;
	}

	// Find option by matching label or value
	const option = options.find((opt: OptionValueProp) => opt.label === searchValue || opt.value === searchValue);

	return option?.value;
}

/**
 * Convert a single Tripetto condition to a JavaScript expression
 * Returns the expression that evaluates to TRUE when the condition is MET
 */
export function convertSingleCondition(condition: TripettoCondition, nodeFieldMap: NodeToFieldMap): string | undefined {
	const block = condition.block;
	if (!block) {
		return undefined;
	}

	// Find the field key being referenced
	const targetSlotId = block.slot;
	if (!targetSlotId) {
		return undefined;
	}

	const fieldKey = nodeFieldMap.getFieldKeyBySlotId(targetSlotId);
	if (!fieldKey) {
		return undefined;
	}

	// Get the actual Formly field to access its real data
	const formlyField = nodeFieldMap.getFieldBySlotId(targetSlotId);

	const conditionType = block.type;
	const mode = block.mode;
	let matchValue = block.match ?? block.value;

	// Handle checkbox and radio button conditions - they have a different structure
	if (conditionType === 'checkboxes-condition2' && block.checkbox) {
		// Use name as fallback when value is empty (matches props-converter.ts logic)
		matchValue = block.checkbox.value || block.checkbox.name;
		const checked = block.checked !== false; // Default to true if not specified

		// For checkboxes, the actual value in Formly options might differ from Tripetto
		// Use the Formly field's options to find the actual value
		const actualValue = findOptionValue(formlyField?.props?.options, matchValue);
		if (actualValue !== undefined) {
			matchValue = actualValue;
		}

		// Checkbox: check if the value is in the array (checkboxes return array of selected values)
		if (checked) {
			return convertContainsCondition(fieldKey, matchValue);
		} else {
			return convertNotContainsCondition(fieldKey, matchValue);
		}
	}

	// Handle radio button conditions (radiobuttons-condition, radiobuttons-condition2)
	if (
		conditionType === 'radiobuttons-condition' ||
		conditionType === 'radiobuttons-condition2' ||
		conditionType === 'radiobuttons-condition3'
	) {
		// For radio buttons, resolve actual Formly option value
		let buttonValue = block.button;
		if (block.button && typeof block.button === 'object') {
			const rawValue = block.button.value || block.button.name;
			const actualValue = findOptionValue(formlyField?.props?.options, rawValue);
			if (actualValue !== undefined) {
				// Update button with actual Formly value
				buttonValue = { ...block.button, value: actualValue };
			}
		} else if (typeof block.button === 'string') {
			const actualValue = findOptionValue(formlyField?.props?.options, block.button);
			if (actualValue !== undefined) {
				buttonValue = actualValue;
			}
		}

		const selectionProps: SelectionConditionProps = {
			button: buttonValue,
			isDynamic: block.isDynamic
		};
		return convertSelectionCondition(fieldKey, selectionProps, conditionType);
	}

	// Handle dropdown conditions (dropdown-condition, dropdown-condition2)
	if (conditionType === 'dropdown-condition' || conditionType === 'dropdown-condition2') {
		// For dropdowns, resolve actual Formly option value if match is specified
		let matchValue = block.match;
		if (matchValue && formlyField?.props?.options) {
			const actualValue = findOptionValue(formlyField?.props?.options, matchValue);
			if (actualValue !== undefined) {
				matchValue = actualValue;
			}
		}

		const selectionProps: SelectionConditionProps = {
			mode: mode as SelectionConditionProps['mode'],
			match: matchValue,
			option: block.option,
			isDynamic: block.isDynamic
		};
		return convertSelectionCondition(fieldKey, selectionProps, conditionType);
	}

	// Handle email conditions (special case with domain/address modes)
	if (conditionType === 'email-condition') {
		const emailProps: EmailConditionProps = {
			mode: mode as EmailConditionProps['mode'],
			match: matchValue
		};
		return convertEmailCondition(fieldKey, emailProps);
	}

	// Handle hidden field string conditions (includes regex support)
	if (conditionType === 'tripetto-block-hidden-field:string') {
		const textProps: TextConditionProps = {
			mode: mode as TextConditionProps['mode'],
			match: block.value || matchValue, // Hidden field uses 'value' instead of 'match'
			ignoreCase: block.ignoreCase,
			regex: block.regex,
			invert: block.invert
		};
		return convertTextCondition(fieldKey, textProps);
	}

	// Handle password conditions (special handling for encrypted comparisons)
	if (conditionType === 'password-condition') {
		// Password exact/not-exact use AES encryption which cannot be converted to Formly
		if (mode === 'exact' || mode === 'not-exact') {
			// Return undefined to trigger a warning - password comparison uses AES encryption
			return undefined;
		}
		// defined/undefined modes are simple and can be converted
		if (mode === 'defined') {
			return `!!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;
		}
		if (mode === 'undefined') {
			return `!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;
		}
		return undefined;
	}

	// Handle text-based conditions (text, url, textarea, phone-number, address, component)
	const textConditionTypes = [
		'text-condition',
		'url-condition',
		'textarea-condition',
		'phone-number-condition',
		'address-condition',
		'component-condition'
	];

	if (textConditionTypes.includes(conditionType || '')) {
		const textProps: TextConditionProps = {
			mode: mode as TextConditionProps['mode'],
			match: matchValue,
			ignoreCase: block.ignoreCase
		};
		return convertTextCondition(fieldKey, textProps);
	}

	// Handle number conditions (including hidden field number)
	if (conditionType === 'number-condition' || conditionType === 'tripetto-block-hidden-field:number') {
		const numberProps: NumberConditionProps = {
			mode: mode as NumberConditionProps['mode'],
			value: block.value,
			to: block.to
		};
		return convertNumberCondition(fieldKey, numberProps);
	}

	// Handle date conditions (date, date-month, and hidden field date)
	if (
		conditionType === 'date-condition' ||
		conditionType === 'date-month-condition' ||
		conditionType === 'tripetto-block-hidden-field:date'
	) {
		const dateProps: DateConditionProps = {
			mode: mode as DateConditionProps['mode'],
			value: block.value,
			to: block.to
		};
		return convertDateCondition(fieldKey, dateProps);
	}

	// Handle hidden field orientation condition (simple equality check)
	if (conditionType === 'tripetto-block-hidden-field:orientation') {
		const orientation = block.orientation;
		if (orientation) {
			return `model['${fieldKey}'] === '${orientation}'`;
		}
		return undefined;
	}

	// Handle yes-no conditions (simple equality checks for 'yes' or 'no')
	if (conditionType === 'tripetto-block-yes-no:yes') {
		return `model['${fieldKey}'] === 'yes'`;
	}

	if (conditionType === 'tripetto-block-yes-no:no') {
		return `model['${fieldKey}'] === 'no'`;
	}

	// Handle simple boolean conditions (checkbox, file-upload)
	if (conditionType === 'checkbox-condition') {
		const simpleProps: SimpleConditionProps = {
			checked: block.checked
		};
		return convertSimpleCondition(fieldKey, simpleProps, conditionType);
	}

	if (conditionType === 'file-upload-condition') {
		const simpleProps: SimpleConditionProps = {
			isUploaded: block.isUploaded
		};
		return convertSimpleCondition(fieldKey, simpleProps, conditionType);
	}

	// Handle chips condition
	if (conditionType === 'chips-condition') {
		// Resolve actual Formly option value for chips
		let buttonValue = block.button;
		if (buttonValue && formlyField?.props?.options) {
			const actualValue = findOptionValue(formlyField?.props?.options, buttonValue);
			if (actualValue !== undefined) {
				buttonValue = actualValue;
			}
		}
		const chipProps: ChipConditionProps = {
			button: buttonValue
		};
		return convertChipCondition(fieldKey, chipProps);
	}

	// Handle mutable-forms (array length) condition
	if (conditionType === 'mutable-forms-condition') {
		const arrayProps: ArrayConditionProps = {
			mode: mode as ArrayConditionProps['mode'],
			value: block.value,
			to: block.to
		};
		return convertArrayCondition(fieldKey, arrayProps);
	}

	// Handle Tripetto block-specific condition types (generic fallback)
	// These use the "mode" field to determine the operation
	if (conditionType && (conditionType.includes('condition') || conditionType.includes('Condition'))) {
		switch (mode) {
			case 'is-matched':
			case 'isMatched':
			case 'match':
				return convertEqualsCondition(fieldKey, matchValue);

			case 'is-not-matched':
			case 'isNotMatched':
			case 'notMatch':
				return convertNotEqualsCondition(fieldKey, matchValue);

			case 'is-empty':
			case 'isEmpty':
			case 'empty':
				return `!model['${fieldKey}']`;

			case 'is-not-empty':
			case 'isNotEmpty':
			case 'notEmpty':
				return `!!model['${fieldKey}']`;

			case 'contains':
				return convertContainsCondition(fieldKey, matchValue);

			case 'not-contains':
			case 'notContains':
				return convertNotContainsCondition(fieldKey, matchValue);

			default:
				// Unknown mode - can't convert
				return undefined;
		}
	}

	// Handle generic condition types (for backwards compatibility)
	switch (conditionType) {
		case 'equals':
		case 'equal':
			return convertEqualsCondition(fieldKey, matchValue);

		case 'not-equals':
		case 'notEqual':
			return convertNotEqualsCondition(fieldKey, matchValue);

		case 'contains':
			return convertContainsCondition(fieldKey, matchValue);

		case 'not-contains':
		case 'notContains':
			return convertNotContainsCondition(fieldKey, matchValue);

		case 'empty':
		case 'isEmpty':
			return `!model['${fieldKey}']`;

		case 'not-empty':
		case 'notEmpty':
		case 'isNotEmpty':
			return `!!model['${fieldKey}']`;

		case 'greater-than':
		case 'greaterThan':
			return `model['${fieldKey}'] > ${matchValue}`;

		case 'less-than':
		case 'lessThan':
			return `model['${fieldKey}'] < ${matchValue}`;

		case 'greater-than-or-equal':
		case 'greaterThanOrEqual':
			return `model['${fieldKey}'] >= ${matchValue}`;

		case 'less-than-or-equal':
		case 'lessThanOrEqual':
			return `model['${fieldKey}'] <= ${matchValue}`;

		default:
			// Unknown condition type - can't convert
			return undefined;
	}
}
